import React, {useEffect, useMemo, useState} from "react";
import Dropdown from 'react-bootstrap/Dropdown';
import YouTube from 'react-youtube';
import {ProfileDialog, ProfileMenu} from './Profiles';
import {
    EMPTY_ENTRY,
    blockKey,
    blockTally,
    dropProgress,
    entryFor,
    loadProfiles,
    loadProgress,
    newId,
    saveProfiles,
    saveProgress,
} from './storage';
import './App.css';

// One cascading selector in the top bar. `options` are {key, label, value, done};
// picking one hands `value` back to onSelect.
function Selector({label, value, placeholder, options, onSelect, disabled, primary, done}) {
    return (
        <Dropdown className={"selector" + (primary ? " selector--primary" : "")}>
            <Dropdown.Toggle as="button" type="button" className="selector-toggle" disabled={disabled}>
                <span className="selector-label">{label}</span>
                <span className={"selector-value" + (value ? "" : " is-empty")}>
                    {done && <Check className="selector-check"/>}
                    {value || placeholder}
                </span>
            </Dropdown.Toggle>
            <Dropdown.Menu>
                {options.map((option) => (
                    <Dropdown.Item
                        as="button"
                        type="button"
                        key={option.key}
                        active={option.label === value}
                        onClick={() => onSelect(option.value)}>
                        {option.done && <Check className="option-check"/>}
                        {option.label}
                    </Dropdown.Item>
                ))}
            </Dropdown.Menu>
        </Dropdown>
    );
}

function Check({className}) {
    return (
        <svg className={className} viewBox="0 0 24 24" width="15" height="15" fill="none"
             stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
             aria-hidden="true">
            <path d="M4 12.5l5.5 5.5L20 6.5"/>
        </svg>
    );
}

function Stat({label, value, text, onPlay, playing}) {
    const empty = value === null || value === undefined || value === "";
    const body = (
        <>
            <span className="stat-label">
                {label}
                {onPlay && (
                    <svg className="stat-play" viewBox="0 0 24 24" width="13" height="13"
                         fill="currentColor" aria-hidden="true">
                        <path d="M8 5.5v13l11-6.5z"/>
                    </svg>
                )}
            </span>
            <span className={"stat-value" + (empty ? " is-empty" : "")}>{empty ? "—" : value}</span>
        </>
    );
    const className = "stat"
        + (text ? " stat--text" : "")
        + (onPlay ? " stat--play" : "")
        + (playing ? " is-playing" : "");

    if (!onPlay) return <div className={className}>{body}</div>;
    return (
        <button type="button" className={className} onClick={onPlay} aria-pressed={!!playing}
                title="Ver la explicación del protocolo">
            {body}
        </button>
    );
}

function Empty({title, hint, inline}) {
    return (
        <div className={"empty" + (inline ? " empty--inline" : "")}>
            <svg className="empty-icon" viewBox="0 0 24 24" width="30" height="30" fill="none"
                 stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
                <path d="M4 9v6M7 6.5v11M17 6.5v11M20 9v6M7 12h10"/>
            </svg>
            <p className="empty-title">{title}</p>
            {hint && <p className="empty-hint">{hint}</p>}
        </div>
    );
}

function App() {

    const [plan, setPlan] = useState({});
    const [plans, setPlans] = useState([]);
    const [weeks, setWeeks] = useState([]);
    const [week, setWeek] = useState(0);
    const [days, setDays] = useState([]);
    const [day, setDay] = useState(0);
    const [activities, setActivities] = useState([]);
    const [activity, setActivity] = useState({});
    const [exercises, setExercises] = useState([]);
    const [selectedExercise, setSelectedExercise] = useState(null);
    const [videoId, setVideoId] = useState("");

    // Profiles and their progress live in this browser only -- see storage.js.
    const [profileState, setProfileState] = useState(loadProfiles);
    const [progress, setProgress] = useState(() => loadProgress(loadProfiles().activeId));
    const [dialog, setDialog] = useState(null);

    const url = process.env.REACT_APP_API_URL;
    const key = process.env.REACT_APP_API_KEY;

    useEffect(() => {
        if (plans.length === 0){
            fetch(url + "/fitness/plans",{
                headers: {
                    "x-api-key": key
                }
            })
                .then(response => response.json())
                .then((data) => {
                    console.log(data);
                    if (data.status === "OK") {
                        setPlans(data.items);
                    }
                });
        }
    }, [plans]);

    useEffect(() => {
        if (plans.length > 0 && plan.id !== 0) {
            fetch(url + "/fitness/weeks?plan=" + plan.id,{
                headers: {
                    "x-api-key": key
                }
            })
                .then(response => response.json())
                .then((data) => {
                    console.log(data);
                    if (data.status === "OK") {
                        setWeeks(data.items);
                    }
                });
        }
    }, [plan]);

    useEffect(() => {
        if (plans.length > 0 && weeks.length > 0 && plan.id !== 0 && week !== 0) {
            fetch(url + "/fitness/days?plan=" + plan.id + "&week=" + week,{
                headers: {
                    "x-api-key": key
                }
            })
                .then(response => response.json())
                .then((data) => {
                    console.log(data);
                    if (data.status === "OK") {
                        setDays(data.items);
                    }
                });
        }
    }, [week]);

    useEffect(() => {
        if (plans.length > 0 && weeks.length > 0 && days.length > 0 && plan.id !== 0 && week !== 0 && day !== 0) {
            fetch(url + "/fitness/activities?plan=" + plan.id + "&week=" + week + "&day=" + day,{
                headers: {
                    "x-api-key": key
                }
            })
                .then(response => response.json())
                .then((data) => {
                    console.log(data);
                    if (data.status === "OK") {
                        setActivities(data.items);
                    }
                });
        }
    }, [day]);

    useEffect(() => {
        if (plans.length > 0 && weeks.length > 0 && days.length > 0
            && plan.id !== 0 && week !== 0 && day !== 0 && activity.id !== undefined) {
                setVideoId(activity.video_id);
                fetch(url + "/fitness/exercises?activity=" + activity.id,{
                    headers: {
                        "x-api-key": key
                    }
                })
                    .then(response => response.json())
                    .then((data) => {
                        console.log(data);
                        if (data.status === "OK") {
                            setExercises(data.items);
                        }
                    });
        }
    }, [activity]);

    // Picking a level invalidates everything below it, so clear the lower levels
    // before the cascading effects refetch them.
    const selectPlan = (nextPlan) => {
        if (nextPlan.id === plan.id) return;
        setPlan(nextPlan);
        setWeeks([]);
        setWeek(0);
        setDays([]);
        setDay(0);
        setActivities([]);
        setActivity({});
        setExercises([]);
        setSelectedExercise(null);
        setVideoId("");
    };

    const selectWeek = (nextWeek) => {
        if (nextWeek === week) return;
        setWeek(nextWeek);
        setDays([]);
        setDay(0);
        setActivities([]);
        setActivity({});
        setExercises([]);
        setSelectedExercise(null);
        setVideoId("");
    };

    const selectDay = (nextDay) => {
        if (nextDay === day) return;
        setDay(nextDay);
        setActivities([]);
        setActivity({});
        setExercises([]);
        setSelectedExercise(null);
        setVideoId("");
    };

    const selectActivity = (nextActivity) => {
        if (nextActivity.id === activity.id) return;
        setActivity(nextActivity);
        setExercises([]);
        setSelectedExercise(null);
        setVideoId("");
    };

    const selectExercise = (exercise, index) => {
        setSelectedExercise(index);
        setVideoId(exercise.video_id);
    };

    // The block carries its own clip explaining the protocol (Super Serie, AMRAP,
    // ...). It plays when the block is picked, and this puts it back afterwards.
    const selectProtocol = () => {
        if (!activity.video_id) return;
        setSelectedExercise(null);
        setVideoId(activity.video_id);
    };

    // ------------------------------------------------------------- profiles

    const active = profileState.profiles.find((profile) => profile.id === profileState.activeId) || null;

    const commitProfiles = (next) => setProfileState(saveProfiles(next));

    const createProfile = (name) => {
        const profile = {id: newId(), name};
        commitProfiles({profiles: [...profileState.profiles, profile], activeId: profile.id});
        setProgress(loadProgress(profile.id));
        setDialog(null);
    };

    const switchProfile = (id) => {
        if (id === profileState.activeId) return;
        commitProfiles({...profileState, activeId: id});
        setProgress(loadProgress(id));
        setDialog(null);
    };

    const renameProfile = (name) => {
        commitProfiles({
            ...profileState,
            profiles: profileState.profiles.map(
                (profile) => (profile.id === active.id ? {...profile, name} : profile)
            ),
        });
        setDialog(null);
    };

    const deleteProfile = () => {
        if (!window.confirm(`¿Borrar el perfil “${active.name}” y todo su progreso?`)) return;
        dropProgress(active.id);
        const rest = profileState.profiles.filter((profile) => profile.id !== active.id);
        const nextId = rest.length > 0 ? rest[0].id : null;
        commitProfiles({profiles: rest, activeId: nextId});
        setProgress(loadProgress(nextId));
    };

    const resetProgress = () => {
        if (!window.confirm(`¿Reiniciar todo el progreso de “${active.name}”?`)) return;
        dropProgress(active.id);
        setProgress({});
    };

    // ------------------------------------------------------------- progress

    const hasPlan = plan.id !== undefined;
    const hasBlock = hasPlan && week !== 0 && day !== 0 && activity.id !== undefined;
    const currentKey = hasBlock ? blockKey(plan.id, week, day, activity.id) : null;
    const entry = currentKey ? entryFor(progress, currentKey) : EMPTY_ENTRY;
    const tally = blockTally(exercises, entry);

    const writeEntry = (key, next) => {
        const merged = {...progress, [key]: next};
        setProgress(merged);
        saveProgress(profileState.activeId, merged);
    };

    const toggleTick = (exerciseId) => {
        if (!currentKey) return;
        const ticks = entry.ticks.includes(exerciseId)
            ? entry.ticks.filter((id) => id !== exerciseId)
            : [...entry.ticks, exerciseId];
        const next = {...entry, ticks};
        // Ticking the last remaining step completes the block. Unticking never
        // un-completes it, so an explicit mark is not undone by accident.
        const after = blockTally(exercises, next);
        if (after.total > 0 && after.done === after.total) next.done = true;
        writeEntry(currentKey, next);
    };

    const toggleBlockDone = () => {
        if (!currentKey) return;
        writeEntry(currentKey, {...entry, done: !entry.done});
    };

    const blockDone = (blockId) => (
        hasPlan && week !== 0 && day !== 0
            ? entryFor(progress, blockKey(plan.id, week, day, blockId)).done
            : false
    );

    // The API sends the prescription as quantity + unit ("30 s", "12 r", "15 r/cl").
    const formatQuantity = (exercise) => (
        [exercise.quantity, exercise.unit]
            .filter((part) => part !== null && part !== undefined && part !== "")
            .join(" ")
    );

    // `loop` is ignored by the YouTube player unless `playlist` names the same
    // video, so a single clip never actually repeated before.
    const opts = useMemo(() => ({
        width: '100%',
        height: '100%',
        playerVars: {
            autoplay: 1,
            loop: 1,
            playlist: videoId,
            rel: 0,
            modestbranding: 1,
        },
    }), [videoId]);

    // ----------------------------------------------------------------- gate

    if (!active) {
        return (
            <ProfileDialog
                title="¿Quién entrena?"
                hint="Tu progreso se guarda en este navegador con el nombre que elijas. Podés crear un perfil por persona."
                confirmLabel="Empezar"
                profiles={profileState.profiles}
                onConfirm={createProfile}/>
        );
    }

    return (
        <div className="app">
            <header className="topbar">
                <div className="brand">
                    <span className="brand-dot" aria-hidden="true"/>
                    <span className="brand-name">Enchin<span className="brand-accent">Fit</span></span>
                </div>
                <nav className="selectors" aria-label="Navegación del plan">
                    <Selector
                        label="Plan"
                        value={plan.name}
                        placeholder={plans.length === 0 ? "Cargando…" : "Elegir plan"}
                        disabled={plans.length === 0}
                        options={plans.map((item) => ({key: item.id, label: item.name, value: item}))}
                        onSelect={selectPlan}/>
                    <Selector
                        label="Semana"
                        value={week !== 0 ? String(week) : ""}
                        placeholder="—"
                        disabled={!hasPlan || weeks.length === 0}
                        options={weeks.map((item) => ({key: item.id, label: "Semana " + item.id, value: item.id}))}
                        onSelect={selectWeek}/>
                    <Selector
                        label="Día"
                        value={day !== 0 ? String(day) : ""}
                        placeholder="—"
                        disabled={week === 0 || days.length === 0}
                        options={days.map((item) => ({key: item.id, label: "Día " + item.id, value: item.id}))}
                        onSelect={selectDay}/>
                    <Selector
                        label="Bloque"
                        value={activity.name}
                        placeholder="—"
                        disabled={day === 0 || activities.length === 0}
                        primary={activity.id !== undefined}
                        done={entry.done}
                        options={activities.map((item) => ({
                            key: item.id,
                            label: item.name,
                            value: item,
                            done: blockDone(item.id),
                        }))}
                        onSelect={selectActivity}/>
                </nav>
                <ProfileMenu
                    profiles={profileState.profiles}
                    active={active}
                    onSelect={switchProfile}
                    onCreate={() => setDialog({mode: "create"})}
                    onRename={() => setDialog({mode: "rename"})}
                    onDelete={deleteProfile}
                    onReset={resetProgress}/>
            </header>

            <main className="main">
                {!hasBlock ? (
                    <Empty
                        title={hasPlan ? "Elegí semana, día y bloque" : `Hola ${active.name}, elegí un plan`}
                        hint={hasPlan
                            ? "Usá los selectores de arriba para llegar al bloque que vas a entrenar."
                            : "Cada plan se abre en semanas, días y bloques de entrenamiento."}/>
                ) : (
                    <>
                        <section className="stats" aria-label="Resumen del bloque">
                            <Stat
                                label="Protocolo"
                                value={activity.protocol}
                                text
                                onPlay={activity.video_id ? selectProtocol : undefined}
                                playing={selectedExercise === null && !!videoId && videoId === activity.video_id}/>
                            <Stat label="Tiempo" value={activity.time}/>
                            <Stat label="Sets" value={activity.sets}/>
                            <Stat label="Descanso" value={activity.rest}/>
                            {exercises.length > 0 && <Stat label="Ejercicios" value={exercises.length}/>}
                        </section>

                        <div className="content">
                            <section className="panel panel--list" aria-label="Ejercicios">
                                {exercises.length > 0 ? (
                                    <>
                                        <ol className="exercises">
                                            {exercises.map((exercise, index) => {
                                                const quantity = formatQuantity(exercise);
                                                const playable = !!exercise.video_id;
                                                const playing = selectedExercise === index;
                                                const ticked = entry.ticks.includes(exercise.a_id);
                                                return (
                                                    <li key={exercise.a_id}>
                                                        <div className={"exercise"
                                                            + (playing ? " is-playing" : "")
                                                            + (ticked ? " is-ticked" : "")}>
                                                            <button
                                                                type="button"
                                                                className={"tick" + (ticked ? " is-on" : "")}
                                                                aria-pressed={ticked}
                                                                aria-label={(ticked ? "Desmarcar " : "Marcar ") + exercise.name}
                                                                onClick={() => toggleTick(exercise.a_id)}>
                                                                {ticked && <Check/>}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="exercise-main"
                                                                disabled={!playable}
                                                                onClick={() => selectExercise(exercise, index)}>
                                                                {quantity !== "" &&
                                                                <span className="exercise-qty">{quantity}</span>}
                                                                <span className="exercise-body">
                                                                    <span className="exercise-name">{exercise.name}</span>
                                                                    {exercise.comments &&
                                                                    <span className="exercise-comments">{exercise.comments}</span>}
                                                                </span>
                                                                {playable &&
                                                                <svg className="exercise-play" viewBox="0 0 24 24"
                                                                     width="18" height="18" fill="currentColor"
                                                                     aria-hidden="true">
                                                                    <path d="M8 5.5v13l11-6.5z"/>
                                                                </svg>}
                                                            </button>
                                                        </div>
                                                    </li>
                                                );
                                            })}
                                        </ol>

                                        <div className="block-footer">
                                            <span className="block-tally">
                                                <span className="block-tally-count">{tally.done}/{tally.total}</span>
                                                {" ejercicios"}
                                            </span>
                                            <button
                                                type="button"
                                                className={"block-done" + (entry.done ? " is-done" : "")}
                                                aria-pressed={entry.done}
                                                onClick={toggleBlockDone}>
                                                {entry.done ? <><Check/> Bloque completo</> : "Marcar bloque completo"}
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <Empty inline title="Este bloque no tiene ejercicios"/>
                                )}
                            </section>

                            <aside className="panel panel--video" aria-label="Video">
                                {videoId ? (
                                    <div className="video-frame">
                                        <YouTube videoId={videoId} opts={opts}/>
                                    </div>
                                ) : (
                                    <div className="video-placeholder">
                                        <svg viewBox="0 0 24 24" width="26" height="26" fill="none"
                                             stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                                            <rect x="2.5" y="5" width="19" height="14" rx="3"/>
                                            <path d="M10 9.5v5l4.5-2.5z" fill="currentColor" stroke="none"/>
                                        </svg>
                                        <span>Tocá un ejercicio para ver el video</span>
                                    </div>
                                )}
                            </aside>
                        </div>
                    </>
                )}
            </main>

            {dialog && (
                <ProfileDialog
                    title={dialog.mode === "rename" ? "Renombrar perfil" : "Nuevo perfil"}
                    hint={dialog.mode === "rename"
                        ? "El progreso guardado se mantiene."
                        : "Cada perfil lleva su propio progreso en este navegador."}
                    initialValue={dialog.mode === "rename" ? active.name : ""}
                    confirmLabel={dialog.mode === "rename" ? "Guardar" : "Crear"}
                    profiles={profileState.profiles}
                    onConfirm={dialog.mode === "rename" ? renameProfile : createProfile}
                    onCancel={() => setDialog(null)}/>
            )}
        </div>
    );
}

export default App;
